/**
 * Transaction Progress Dialog
 *
 * Centered dialog for showing transaction progress.
 * Replaces corner toasts for better visibility during transactions.
 */

import { useEffect, useCallback } from 'react';
import { useTransactionDialog } from '@/hooks/useTransactionDialog';
import { TransactionProgressContent } from './TransactionProgressContent';
import { cn } from '@/lib/utils';

export function TransactionProgressDialog() {
  const { isOpen, state, dismiss } = useTransactionDialog();

  // Handle ESC key - only dismiss when not executing
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && state !== 'executing') {
        dismiss();
      }
    },
    [state, dismiss]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  // Handle backdrop click - only dismiss when not executing
  const handleBackdropClick = useCallback(() => {
    if (state !== 'executing') {
      dismiss();
    }
  }, [state, dismiss]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-[60] bg-black/80',
          'animate-in fade-in-0 duration-200'
        )}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div
        className={cn(
          'fixed z-[60] left-1/2 top-[20%] -translate-x-1/2',
          'w-full max-w-[450px] mx-4 sm:mx-0',
          'animate-in fade-in-0 zoom-in-95 slide-in-from-top-4 duration-200'
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="transaction-dialog-title"
      >
        <div className="bg-background border border-border rounded-lg shadow-2xl overflow-hidden">
          <TransactionProgressContent />
        </div>
      </div>
    </>
  );
}
